/**
 * The built-in dimension annotation (see ObjectDimension) as a data spec: one
 * parametrized dimension covering the usual plan conventions — the end
 * symbol, a solid or dashed line and the extension lines are options of the
 * placed dimension, so most drawings never need an own one. The dimension
 * tool places it; organisations duplicate it to make their own.
 *
 * Frame: the measured points at `-width/2` and `+width/2` on n = 0. The line
 * stretches with the measured length; the end symbols keep their size and
 * stick to their end (`pinStart` / `pinEnd` along u, `fixed` across). Line and
 * end symbols shift to the placed `offset`; the extension lines run from just
 * off the measured points to just past the line (none at offset 0). Each
 * option shows its parts with a `visibleWhen` group.
 */

import { DEFAULT_DIMENSION_SIZE, OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type ObjectGroup, type ObjectShape, type Vec2 } from '../spec';

const HW = DEFAULT_DIMENSION_SIZE.width / 2;
const HD = DEFAULT_DIMENSION_SIZE.depth / 2;
/** Half the size of a 45° tick. */
const TICK = 0.075;
/** Arrowhead length along the line and half its width. */
const ARROW_LENGTH = 0.15;
const ARROW_HALF_WIDTH = 0.05;
const DOT_RADIUS = 0.03;
/** Half the length of a straight end bar. */
const BAR = 0.1;
/** Extension lines: the gap they leave at the measured point and how far they run past the line. */
const EXTENSION_GAP = 0.05;
const EXTENSION_OVERSHOOT = 0.1;

type Side = 'start' | 'end';

/** `[u, n]` with u mirrored to the end side (authored at the start, −u). */
const at = (side: Side, u: number, n: number): Vec2 => [side === 'start' ? u : -u, n];

const line = (id: string, name: string, points: Vec2[], closed = false): ObjectShape => ({ id, name, kind: 'polyline', points, closed });

/** The end symbols, by their `endStyle` value (shapes authored at one end). */
const END_SYMBOLS: ReadonlyArray<{ value: string; label: string; shape: (side: Side) => ObjectShape }> = [
  {
    value: 'tick',
    label: 'ticks',
    // Both ticks lean the same way (not mirrored), the plan convention.
    shape: (side) => {
      const u = side === 'start' ? -HW : HW;
      return line(`${side}Tick`, `Tick (${side})`, [
        [u - TICK, -TICK],
        [u + TICK, TICK],
      ]);
    },
  },
  { value: 'arrow', label: 'arrows', shape: (side) => line(`${side}Arrow`, `Arrow (${side})`, [at(side, -HW, 0), at(side, -HW + ARROW_LENGTH, ARROW_HALF_WIDTH), at(side, -HW + ARROW_LENGTH, -ARROW_HALF_WIDTH)], true) },
  { value: 'openArrow', label: 'open arrows', shape: (side) => line(`${side}OpenArrow`, `Open arrow (${side})`, [at(side, -HW + ARROW_LENGTH, ARROW_HALF_WIDTH), at(side, -HW, 0), at(side, -HW + ARROW_LENGTH, -ARROW_HALF_WIDTH)]) },
  { value: 'dot', label: 'dots', shape: (side) => ({ id: `${side}Dot`, name: `Dot (${side})`, kind: 'circle', center: at(side, -HW, 0), radius: DOT_RADIUS }) },
  { value: 'bar', label: 'straight ends', shape: (side) => line(`${side}Bar`, `End bar (${side})`, [at(side, -HW, -BAR), at(side, -HW, BAR)]) },
];

const pin = (side: Side): ObjectGroup['resize'] => ({ u: side === 'start' ? 'pinStart' : 'pinEnd', n: 'fixed' });

/** The line and the end symbols sit at the placed offset. */
const AT_OFFSET: ObjectGroup['offset'] = { n: 'offset' };

const SIDES: readonly Side[] = ['start', 'end'];

const endShapes = END_SYMBOLS.flatMap((symbol) => SIDES.map((side) => ({ symbol, side, shape: symbol.shape(side) })));
/** The extension line styles, by their `extensionLines` value (plus `none`). */
const EXTENSION_STYLES: ReadonlyArray<{ value: 'dashed' | 'solid'; suffix: string }> = [
  { value: 'dashed', suffix: '' },
  { value: 'solid', suffix: 'Solid' },
];
const extensionShapes = EXTENSION_STYLES.flatMap(({ value, suffix }) =>
  SIDES.map((side) => ({
    side,
    style: value,
    shape: {
      ...line(`${side}Extension${suffix}`, `Extension line (${side}${suffix ? ', solid' : ''})`, [at(side, -HW, -HD), at(side, -HW, HD)]),
      expr: { 'p0.n': `sign(offset) * ${EXTENSION_GAP}`, 'p1.n': `offset + sign(offset) * ${EXTENSION_OVERSHOOT}` },
      ...(value === 'dashed' ? { style: 'dashed' as const } : {}),
    },
  })),
);

/** The built-in dimension: ticks and dashed extension lines by default; end symbol, line style and extension lines are options. */
export const dimensionSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'FREE' },
  drawLikeWall: true,
  defaultSize: { ...DEFAULT_DIMENSION_SIZE },
  params: [
    { key: 'endStyle', kind: 'select', label: 'ends', default: 'tick', options: [...END_SYMBOLS.map(({ value, label }) => ({ value, label })), { value: 'none', label: 'none' }] },
    {
      key: 'lineStyle',
      kind: 'select',
      label: 'line',
      default: 'solid',
      options: [
        { value: 'solid', label: 'solid' },
        { value: 'dashed', label: 'dashed' },
      ],
    },
    {
      key: 'extensionLines',
      kind: 'select',
      label: 'extension lines',
      default: 'dashed',
      options: [
        { value: 'dashed', label: 'dashed' },
        { value: 'solid', label: 'solid' },
        { value: 'none', label: 'none' },
      ],
    },
  ],
  shapes: [
    line('line', 'Dimension line', [at('start', -HW, 0), at('end', -HW, 0)]),
    { ...line('dashedLine', 'Dimension line (dashed)', [at('start', -HW, 0), at('end', -HW, 0)]), style: 'dashed' },
    ...extensionShapes.map(({ shape }) => shape),
    ...endShapes.map(({ shape }) => shape),
  ],
  groups: [
    { id: 'line', name: 'Dimension line', shapeIds: ['line'], resize: { u: 'stretch', n: 'fixed' }, visibleWhen: { param: 'lineStyle', equals: 'solid' }, offset: AT_OFFSET },
    { id: 'dashedLine', name: 'Dimension line (dashed)', shapeIds: ['dashedLine'], resize: { u: 'stretch', n: 'fixed' }, visibleWhen: { param: 'lineStyle', equals: 'dashed' }, offset: AT_OFFSET },
    // Only when the line is pulled off the measured points.
    ...extensionShapes.map(
      ({ side, style, shape }): ObjectGroup => ({ id: shape.id, name: shape.name ?? shape.id, shapeIds: [shape.id], resize: pin(side), visibleWhen: { param: 'extensionLines', equals: style }, visibleIf: 'abs(offset) > 0.001' }),
    ),
    ...endShapes.map(({ symbol, side, shape }): ObjectGroup => ({ id: shape.id, name: shape.name ?? shape.id, shapeIds: [shape.id], resize: pin(side), visibleWhen: { param: 'endStyle', equals: symbol.value }, offset: AT_OFFSET })),
  ],
  model3d: null,
  dimension: { kind: 'LINEAR' },
};

/** The built-in dimension the tool arms first (SCHEMA: persisted in placed dimensions). */
export const DEFAULT_DIMENSION_TYPE_ID = 'dimension';

/** Built-in dimension type id → its spec and name, in library order. */
export const DATA_BUILTIN_DIMENSION_SPECS: ReadonlyArray<{ id: string; label: string; spec: ObjectDefinitionSpec }> = [{ id: DEFAULT_DIMENSION_TYPE_ID, label: 'Dimension', spec: dimensionSpec }];
